'use strict'

import { removeElement } from './functions/dom.js'

const parseRmUrl = require('./functions/parseRmUrl.js')
const getMeta = require('./functions/getMeta.js')

//Load modules
require('./lib/angular')

Element.prototype.hide = function hide () {
    this.classList.add('hide')
}

Element.prototype.show = function show () {
    this.classList.remove('hide')
}

const app = angular.module('rmApp', [
    'ngResource',
    'ngAnimate',
    'ngCookies',
    'ngMessages',
    'ngMdIcons',
    'ui.gravatar',
    'ui.sortable',
    'treeControl',
    'angularUtils.directives.dirPagination',
    'jlareau.bowser',
    'ui.ace',
    'dndLists',
    'ngSanitize',
    'minicolors',
    'ngFileUpload',
    'vcRecaptcha',
    'ui.router',
    'ngMaterial',

    // RM modules
    'rmAuth',
    'rmCampaign',
    'rmBlades',
    'rmContact',
    'rmDashboard',
    'rmEPG',
    'rmItemPlanner',
    'rmOrganisation',
    'rmPresenter',
    'rmProgram',
    'rmRole',
    'rmRundown',
    'rmSidebar',
    'rmStationSettings',
    'rmTemplate',
    'rmToolbar',
    'rmToken',
    'rmUser',
    'rmTrashcan',
    'rmGroups',
    'rmBroadcast',
])

//Includes
require('./modules/app')
require('./modules/auth')
require('./modules/blades')
require('./modules/campaign')
require('./modules/contact')
require('./modules/dashboard')
require('./modules/epg')
require('./modules/formbuilder')
require('./modules/item-planner')
require('./modules/oauth')
require('./modules/organisation')
require('./modules/presenter')
require('./modules/program')
require('./modules/role')
require('./modules/rundown')
require('./modules/sidebar')
require('./modules/stationsettings')
require('./modules/template')
require('./modules/toolbar')
require('./modules/user')
require('./modules/trashcan')
require('./modules/groups')
require('./modules/broadcast')

// * Remove debug info from DOM
// https://code.angularjs.org/1.5.5/docs/guide/production
app.config(['$compileProvider', function ($compileProvider) {
    $compileProvider.debugInfoEnabled(process.env.NODE_ENV !== 'production')
}])

if (window.CONFIG) {

    moment.locale(window.CONFIG.language)

    app.config(['$mdDateLocaleProvider', function ($mdDateLocaleProvider) {
        $mdDateLocaleProvider.months = moment.months()
        $mdDateLocaleProvider.shortMonths = moment.monthsShort()

        $mdDateLocaleProvider.days = moment.weekdays()
        $mdDateLocaleProvider.shortDays = moment.weekdaysShort()

        $mdDateLocaleProvider.firstDayOfWeek = moment.localeData().firstDayOfWeek()

        $mdDateLocaleProvider.formatDate = function (date) {
            return date ? moment(date).format('L') : null
        }

        $mdDateLocaleProvider.parseDate = function (dateString) {
            let m = moment(dateString, 'L')
            return m.isValid() ? m.toDate() : new Date(NaN)
        }
    }])

    app.service('moment', () => moment)
    app.filter('amDateFormatTZ', () => {
        return (input, format, timezone) => {
            let dateTime
            if (!input) {
                return ''
            } else if (input === 'now') {
                dateTime = moment()
            } else {
                dateTime = moment(input)
            }
            return dateTime
                .tz(timezone || window.CONFIG.timezone)
                .format(format)
        }
    })
    app.filter('amDateFormat', () => {
        return (input, format) => {
            if (!input) return ''
            return moment(input).format(format)
        }
    })
}

/**
 * Default action
 */
app.config([
    '$stateProvider', '$urlRouterProvider', '$mdThemingProvider', '$httpProvider', 'paginationTemplateProvider',
    function ($stateProvider, $urlRouterProvider, $mdThemingProvider, $httpProvider, paginationTemplateProvider) {
        $urlRouterProvider.otherwise('/')
        $httpProvider.interceptors.push('authenticationInterceptor')

        $httpProvider.interceptors.push(['$q', function ($q) {
            return {
                'request': function (config) {
                    if (
                        !/^https?:\/\//.test(config.url) &&
                        !/\.ngTemplate$/.test(config.url)
                    ) {
                        config.url = parseRmUrl(config.url).href
                    }

                    return config || $q.when(config)
                }
            }
        }])

        // Populate the standard HTTP headers with the laravel CSRF TOKEN
        $httpProvider.defaults.headers.common['X-CSRF-TOKEN'] = getMeta('csrf-token')

        $mdThemingProvider.definePalette('rmPrimaryPallet', {
            '50': 'fff000',
            '100': 'e7f2d9',
            '200': 'ef9a9a',
            '300': 'e57373',
            '400': 'ef5350',
            '500': '86be40',
            '600': 'f062a4',
            '700': 'd32f2f',
            '800': 'c62828',
            '900': 'b71c1c',
            'A100': 'ff8a80',
            'A200': 'ff5252',
            'A400': 'ff1744',
            'A700': 'd50000',
            'contrastDefaultColor': 'light',    // whether, by default, text (contrast)
                                                // on this palette should be dark or light
            'contrastDarkColors': ['50', '100', //hues which contrast should be 'dark' by default
                '200', '300', '400', 'A100'],
            'contrastLightColors': undefined    // could also specify this if default was 'dark'
        })

        $mdThemingProvider.theme('default').primaryPalette('rmPrimaryPallet')

        paginationTemplateProvider.setPath('/partial/app.helpers.pagination')

    }
])

app.factory('authenticationInterceptor', ['$q', '$injector', function ($q, $injector) {

    // The reload below used to be unbounded. Combined with remember-me - which keeps /main returning
    // 200 forever, so the page always boots again - one stale CSRF token reloaded the app roughly once
    // a second for two hours, minting a session every time until Redis hit its cap. Cap the reloads.
    const RELOAD_GUARD_KEY = 'rmReloadGuard'
    const MAX_RELOADS = 3
    const RELOAD_WINDOW_MS = 30000

    function reloadAllowed () {
        try {
            const now = Date.now()
            const recent = JSON.parse(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || '[]')
                .filter(function (t) { return now - t < RELOAD_WINDOW_MS })

            if (recent.length >= MAX_RELOADS) {
                console.error('Suppressed reload: ' + recent.length + ' reloads in the last ' +
                    (RELOAD_WINDOW_MS / 1000) + 's. Session is probably not persisting.')
                return false
            }

            recent.push(now)
            window.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(recent))
            return true
        } catch (e) {
            // No sessionStorage (private mode, quota). Allow the reload rather than wedge the app.
            return true
        }
    }

    return {
        request: function (config) {
            config.headers['ANGULAR-REQUEST'] = 'true'
            return config
        },
        responseError: function (response) {
            const config = response.config || {}

            // 419 = the token this page booted with no longer matches the session. Fetch a fresh one
            // and replay the request once. Never reload for this: the reload is what replaces the
            // session, which invalidates the new token too.
            if (response.status === 419 && !config.csrfRetried) {
                config.csrfRetried = true

                // Resolved lazily - $http depends on the interceptors, so injecting it up front
                // would be circular.
                const $http = $injector.get('$http')

                return $http.get('/csrf-token').then(function (res) {
                    const token = res.data && res.data.token
                    if (!token) {
                        return $q.reject(response)
                    }

                    $http.defaults.headers.common['X-CSRF-TOKEN'] = token
                    config.headers = config.headers || {}
                    config.headers['X-CSRF-TOKEN'] = token

                    return $http(config)
                }, function () {
                    return $q.reject(response)
                })
            }

            // If we are not on the login screen redirect the user to it.
            if (window.location.pathname === '/main') {
                if (response.status === 401 || response.status === 403) {
                    if (reloadAllowed()) {
                        window.location = '/'
                    }
                }
            }

            return $q.reject(response)
        }
    }
}])

/**
 * Multi Views (Blades) en Routes Management
 */
app.factory('bladesManager', ['$timeout', function ($timeout) {

    function exists (name, level) {
        for (let i = 0, l = blades[level].length; i < l; i++) {
            if (name === blades[level][i].viewName) {
                return true
            }
        }
        return false
    }

    function removeIfExists (obj, level) {
        if (Array.isArray(blades[level])) {
            for (let i = 0, l = blades[level].length; i < l; i++) {
                if (obj.viewName === blades[level][i].viewName) {
                    blades[level].splice(i, 1)
                    break
                }
            }
        }
    }

    function newBlade (obj, level) {
        // Check for existance in the current level
        removeIfExists(obj, level)

        // Give an unique view ID
        obj.id = ids
        ids++

        // Insert the new card
        obj.newCard = blades[level].length < 2
        blades[level].push(obj)
    }

    function calculateContainer (level, removing) {

        // 2 basic blades + 1 = 3
        let bladesOnScreen = removing ? 3 : 2
        let totalBlades = (blades[level].length - bladesOnScreen) < 0 ? 0 : (blades[level].length - bladesOnScreen)

        let x = 0
        for (let i = 0, l = totalBlades; i < l; i++) {
            x += (sizes[blades[level][i].size])
        }

        getContainer(level).css({ transform: 'translateX( -' + (x) + '% )' })
    }

    function getBlades (level) {
        return blades[level] ? blades[level] : []
    }

    function getContainer (level) {
        let con = $('.cards-scroll-container.level-' + level)
        return con ? con : null
    }

    function addNewLevel (level) {
        if (!blades[level]) {
            let newLayer = []
            // Give an unique view ID
            newLayer.id = ids
            ids++

            if (level === 0) {
                newLayer.reset = true
                $timeout(() => {
                    newLayer.reset = false
                }, 500)
            }
            blades[level] = newLayer
        }
    }

    function removeLevel (level) {
        blades.splice(level, 1)
    }

    let sizes = {
        'none': 0,
        'one-third': 22.5,
        'full-one-third': 24.5,
        'half-floating': 25, // @todo hack for floating half with more than 2 panels open
        'half': 50,
        'full': 100,
    }
    let ids = 100 // We need a key to keep track of the added levels
    let blades = []
    let timer

    let previousState = ''
    let oldParams = null,
        newParams = null

    addNewLevel(0)
    return {

        all: function (level) {
            if (typeof level == 'number') {
                return getBlades(level)
            } else {
                return blades
            }
        },

        insert: function (obj, level = 0) {
            if (!blades[level]) {
                addNewLevel(level)

                $timeout(() => {
                    newBlade(obj, level)
                })
            } else {
                newBlade(obj, level)
            }

        },

        insertRight: function (obj, level = 0) {
            return new Promise(resolve => {

                // Insert
                this.insert(obj, level)

                // Wait for animation
                $timeout.cancel(timer)
                timer = $timeout(() => {
                    if (blades[level]) {
                        blades[level].forEach((blade) => {
                            blade.newCard = true
                            blade.oldCard = false
                        })

                        for (let i = (blades[level].length - 3); i >= 0; i--) {
                            blades[level][i].oldCard = true
                            blades[level][i].newCard = false
                        }
                    }
                    resolve()
                }, 250)

                calculateContainer(level)
            })
        },

        removeRight: function (name, level = 0) {
            return new Promise(resolve => {
                if (!exists(name, level)) {
                    resolve(false)
                }

                if (blades[level] && blades[level].length === 1) {
                    resolve(removeLevel(level))
                }

                let oldCardIndex = blades[level].length - 3 < 0 ? 0 : (blades[level].length - 3)

                blades[level][blades[level].length - 1].newCard = false
                blades[level][oldCardIndex].oldCard = false
                blades[level][oldCardIndex].newCard = true

                // Wait for animation
                $timeout(function () {
                    removeIfExists({
                        viewName: name
                    }, level)
                    resolve()
                }, 400)

                calculateContainer(level, true)
            })
        },

        getFloatingState: function () {
            return blades[1] ? blades[1].length > 0 : false
        },

        insertFloating: function (obj) {
            return this.insertRight(obj, 1)
        },

        removeAllFloating: function () {
            removeLevel(1)
        },

        removeAll: function (level = 0) {
            removeLevel(level)
        },

        clearAll: function (cb) {
            getContainer(0).addClass('reset')

            if (blades[0]) {
                blades[0].splice(0, blades[0].length)
                calculateContainer(0)
            }
            blades.splice(1, blades.length)

            $timeout(() => {
                getContainer(0).removeClass('reset')
                cb ? cb() : null
            })
        },

        setPreviousState: function (from) {
            previousState = from
        },

        getPreviousState: function () {
            return previousState
        },

        setStateParams: function (from, to) {
            oldParams = from
            newParams = to
        },

        getStateParams: function () {
            return {
                old: oldParams,
                new: newParams
            }
        }
    }
}])

window.dndCleanupFix = selector => {
    setTimeout(() => { // Fix for removing the ghost items. The dnd lib does not do this well
        const dndContainer = document.querySelector(selector)

        Array.from(dndContainer.getElementsByClassName('dndPlaceholder'))
            .forEach(removeElement)

        Array.from(dndContainer.getElementsByClassName('dndDragover'))
            .forEach(element => element.classList.remove('dndDragover'))
    })
}
